<template>
  <transition name="login-switch" mode="out-in">
    <TransicionLogin
      v-if="showGameLoading"
      key="loading"
      text="Preparando tu compañía energética..."
    />

    <v-container
      v-else
      key="signup"
      class="signup-page fill-height d-flex align-center justify-center"
      fluid
    >
      <div class="signup-bg-grid"></div>

      <v-card class="signup-card pa-6" max-width="430" width="100%">
        <div class="signup-logo">
          <span class="material-symbols-outlined filled">
            bolt
          </span>
        </div>

        <div class="signup-title">
          Completa tu registro
        </div>

        <div class="signup-subtitle">
          Elige tu nombre de jugador y empieza a competir en el mercado eléctrico.
        </div>

        <v-alert v-if="error" type="error" class="mb-3 signup-alert">
          {{ error }}
        </v-alert>

        <v-text-field
          v-model="username"
          label="Nombre de usuario"
          variant="outlined"
          autocomplete="username"
          required
          class="signup-input"
        />

        <v-btn
          block
          class="signup-btn mt-3"
          :loading="loading"
          :disabled="loading || !google_sub"
          @click="submitSignup"
        >
          Crear cuenta
        </v-btn>
      </v-card>
    </v-container>
  </transition>
</template>

<script>
import axios from "axios";
import { useGameStore } from "@/stores/game";
import TransicionLogin from "./TransicionLogin.vue";

export default {

  components: {
    TransicionLogin,
  },

  data() {
    return {
      username: "",
      google_sub: null,
      loading: false,
      showGameLoading: false,
      error: null,
      store: useGameStore(),
    };
  },

  mounted() {
    this.google_sub = this.$route.query.google_sub;

    if (!this.google_sub) {
      this.error = "Falta información de Google. Vuelve a iniciar sesión.";
    }
  },

  methods: {
    async submitSignup() {
      this.error = null;

      const cleanUsername = (this.username || "").trim();

      if (!cleanUsername) {
        this.error = "Debes introducir un nombre de usuario";
        return;
      }

      this.loading = true;

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/auth/signup`,
          {
            google_sub: this.google_sub,
            username: cleanUsername,
          }
        );

        const payload = res.data;

        if (payload.status !== "success") {
          throw new Error(payload.message || "Error en signup");
        }

        const { token, user } = payload.data;

        if (!token || !user) {
          throw new Error("No se recibió sesión válida");
        }

        this.store.setAuth(token, user);

        this.showGameLoading = true;

        await this.store.loadAll();

        setTimeout(() => {
          this.$router.push("/");
        }, 1400);

      } catch (e) {
        this.showGameLoading = false;
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "Error creando cuenta";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.signup-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 20% 15%, rgba(34, 197, 94, 0.22), transparent 28%),
    radial-gradient(circle at 85% 20%, rgba(56, 189, 248, 0.22), transparent 30%),
    linear-gradient(135deg, #06111f 0%, #0f172a 55%, #111827 100%);
}

.signup-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 44px 44px;
  opacity: 0.55;
}

.signup-card {
  position: relative;
  z-index: 1;
  border-radius: 30px !important;
  color: white;
  background: rgba(255, 255, 255, 0.09) !important;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(18px);
}

.signup-logo {
  width: 78px;
  height: 78px;
  margin: 0 auto 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, #bfffe2 0%, #22c55e 45%, #2563eb 100%);
  box-shadow:
    0 0 24px rgba(34, 197, 94, 0.75),
    0 0 58px rgba(56, 189, 248, 0.42);
}

.signup-logo span {
  font-size: 42px;
  color: #06111f;
}

.signup-title {
  text-align: center;
  font-family: "Orbitron", sans-serif !important;
  font-size: 25px;
  font-weight: 800;
  letter-spacing: 0.6px;
  color:white;
}

.signup-subtitle {
  max-width: 330px;
  margin: 10px auto 24px;
  text-align: center;
  font-size: 14px;
  color: rgba(255,255,255,0.72);
}

.signup-btn {
  height: 46px !important;
  border-radius: 999px !important;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #06111f !important;
  background: linear-gradient(90deg, #22c55e, #38bdf8) !important;
  box-shadow: 0 0 28px rgba(56, 189, 248, 0.42);
}

.signup-alert {
  border-radius: 16px;
}

.login-switch-enter-active,
.login-switch-leave-active {
  transition: opacity 0.45s ease, transform 0.45s ease;
}

.login-switch-enter,
.login-switch-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

/* Ajustes input */
::v-deep .signup-input .v-field,
::v-deep .signup-input .v-input__control,
::v-deep .signup-input .v-input__slot {
  background: rgba(255,255,255,0.08) !important;
  border-radius: 16px !important;
}

::v-deep .signup-input input {
  color: white !important;
}

::v-deep .signup-input label {
  color: rgba(255,255,255,0.72) !important;
}

::v-deep .signup-input .v-field__outline,
::v-deep .signup-input .v-field__overlay {
  border-color: rgba(255,255,255,0.18) !important;
}

@media (max-width: 600px) {
  .signup-card {
    padding: 24px 20px !important;
  }

  .signup-title {
    font-size: 22px;
  }

  .signup-subtitle {
    font-size: 13px;
  }
}
</style>